import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import axios from 'axios'
import { API_BASE_URL } from '../../utils/api'
import LoadingSpinner from '../LoadingSpinner'
import { useTranslation } from 'react-i18next'

interface Department {
  id: number
  name: string
  description: string
  color: string
  lead_user_id?: number
  lead_first_name?: string
  lead_last_name?: string
  lead_email?: string
  member_count: number
  is_active: boolean
  created_at: string
}

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  email: string
  role: string
}

const DEFAULT_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
]

export const DepartmentManager: React.FC = () => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [departments, setDepartments] = useState<Department[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    leadUserId: ''
  })

  useEffect(() => {
    fetchDepartments()
    fetchTeamMembers()
  }, [])

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/team-management/departments`)
      setDepartments(response.data.departments)
    } catch (error) {
      console.error('Error fetching departments:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/team-management/overview`)
      const members = response.data.data.teamMembers.filter((member: any) => 
        member.role === 'collaborator' && member.collaborator_status === 'active'
      )
      setTeamMembers(members)
    } catch (error) {
      console.error('Error fetching team members:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        leadUserId: formData.leadUserId ? parseInt(formData.leadUserId) : undefined
      }

      if (editingDepartment) {
        await axios.put(`${API_BASE_URL}/api/team-management/departments/${editingDepartment.id}`, payload)
      } else {
        await axios.post(`${API_BASE_URL}/api/team-management/departments`, payload)
      }

      await fetchDepartments()
      resetForm()
    } catch (error: any) {
      console.error('Error saving department:', error)
      alert(error.response?.data?.message || t('teamManagement.departmentManager.failedToSave'))
    } finally {
      setSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      leadUserId: ''
    })
    setShowCreateForm(false)
    setEditingDepartment(null)
  }

  const startEdit = (department: Department) => {
    setFormData({
      name: department.name,
      description: department.description || '',
      color: department.color,
      leadUserId: department.lead_user_id?.toString() || ''
    })
    setEditingDepartment(department)
    setShowCreateForm(true)
  }

  const deactivateDepartment = async (departmentId: number) => {
    if (!confirm(t('teamManagement.departmentManager.deactivateConfirm'))) {
      return
    }

    try {
      await axios.put(`${API_BASE_URL}/api/team-management/departments/${departmentId}`, {
        isActive: false
      })
      await fetchDepartments()
    } catch (error) {
      console.error('Error deactivating department:', error)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('teamManagement.departmentManager.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('teamManagement.departmentManager.description')}
          </p>
        </div>
        {departments.length > 0 && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('teamManagement.departmentManager.newDepartment')}
          </button>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingDepartment ? t('teamManagement.departmentManager.editDepartment') : t('teamManagement.departmentManager.createNewDepartment')}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
            
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.departmentManager.departmentNameRequired')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    placeholder={t('teamManagement.departmentManager.departmentNamePlaceholder')}
                    required
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.departmentManager.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    rows={3}
                    placeholder={t('teamManagement.departmentManager.descriptionPlaceholder')}
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.departmentManager.departmentColor')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('teamManagement.departmentManager.custom')}</span>
                    </div>
                    <div className="flex space-x-1">
                      {DEFAULT_COLORS.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-lg border-2 transition-all ${
                            formData.color === color 
                              ? 'border-gray-800 dark:border-white scale-110' 
                              : 'border-gray-200 dark:border-gray-600 hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('teamManagement.departmentManager.departmentLead')}
                  </label>
                  <select
                    value={formData.leadUserId}
                    onChange={(e) => setFormData(prev => ({ ...prev, leadUserId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  >
                    <option value="">{t('teamManagement.departmentManager.noLeadAssigned')}</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.first_name} {member.last_name} • {member.email}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </div>

            <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                disabled={submitting}
              >
                {t('teamManagement.departmentManager.cancel')}
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                {submitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('teamManagement.departmentManager.saving')}</span>
                  </div>
                ) : (
                  editingDepartment ? t('teamManagement.departmentManager.updateDepartment') : t('teamManagement.departmentManager.createDepartment')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Departments Grid */}
      {departments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {departments.map(department => (
            <div key={department.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
              {/* Department Header with Color Stripe */}
              <div className="relative">
                <div 
                  className="h-1"
                  style={{ backgroundColor: department.color }}
                ></div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: department.color }}
                        ></div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{department.name}</h3>
                      </div>
                      {department.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{department.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => startEdit(department)}
                        className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title={t('teamManagement.departmentManager.editTooltip')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => deactivateDepartment(department.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title={t('teamManagement.departmentManager.deactivateTooltip')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Department Stats Cards */}
                  <div className="space-y-4">
                    {/* Member Count */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('teamManagement.departmentManager.teamMembers')}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {department.member_count}
                      </span>
                    </div>

                    {/* Department Lead */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('teamManagement.departmentManager.departmentLead')}</span>
                      </div>
                      {department.lead_first_name && department.lead_last_name ? (
                        <div className="ml-10">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {department.lead_first_name} {department.lead_last_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {department.lead_email}
                          </div>
                        </div>
                      ) : (
                        <div className="ml-10 text-sm text-gray-500 dark:text-gray-400">
                          {t('teamManagement.departmentManager.noLeadAssigned')}
                        </div>
                      )}
                    </div>

                    {/* Status and Created Date */}
                    <div className="flex items-center justify-between pt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        department.is_active 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          department.is_active ? 'bg-green-400' : 'bg-red-400'
                        }`}></div>
                        {department.is_active ? t('teamManagement.departmentManager.active') : t('teamManagement.departmentManager.inactive')}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('teamManagement.departmentManager.created')} {new Date(department.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
            </svg>
          </div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">{t('teamManagement.departmentManager.noDepartmentsTitle')}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
            {t('teamManagement.departmentManager.noDepartmentsDescription')}
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('teamManagement.departmentManager.createFirstDepartment')}
          </button>
        </div>
      )}
    </div>
  )
}
